import { Dispatch, memo, ReactNode, RefObject, SetStateAction, useRef, useState } from 'react';

import { mergeRefs } from '@/utils/merge-refs';
import useHandleSyncValueAndClearActiveStates from './helpers/use-handle-sync-value-and-clear-activ-states';
import useHandleFocusInputOnClick from './helpers/use-handle-focus-input-on-click';
import useHandleSelectWordAtCaret from './helpers/use-handle-select-word-at-caret';
import useHandleArrowKeyAnchorSync from './helpers/use-handle-arrow-key-anchor-sync';
import useHandleManualCursorAndSelection from './helpers/use-handle-manual-cursor-and-selection';
import useHandleChange from './helpers/use-handle-change';
import useHandleBlur from './helpers/use-handle-blur';
import useHandleFocus from './helpers/use-handle-focus';

import styles from '../../custom-select.module.css';

type CustomSelectInputProps = {
  inputRef: RefObject<HTMLInputElement | null>;
  menuRef: RefObject<HTMLDivElement | null>;
  selectedOptionsRef: RefObject<HTMLDivElement | null>;
  id: string;
  Placeholder?: string | ReactNode | (() => ReactNode);
  lockActiveState: boolean;
  hasSelectedOptions: boolean;
  setIsFocused: Dispatch<SetStateAction<boolean>>;
  setMenuOpen: Dispatch<SetStateAction<boolean>>;
  setSearchValue: Dispatch<SetStateAction<string>>;
  propsInputRef?: RefObject<HTMLInputElement | null>;
  disabled?: boolean;
  hidePlaceholderOnInputFocus?: boolean;
  isSearchable?: boolean;
  hidePlaceholderIfInputHasValue?: boolean;
};

const CustomSelectInput = memo(
  ({
    id,
    inputRef,
    Placeholder,
    setIsFocused,
    setSearchValue,
    menuRef,
    selectedOptionsRef,
    setMenuOpen,
    hasSelectedOptions,
    lockActiveState,
    propsInputRef,
    disabled,
    hidePlaceholderOnInputFocus,
    hidePlaceholderIfInputHasValue,
    isSearchable = true,
  }: CustomSelectInputProps) => {
    // tracks the anchor position for shift+click text selection
    const selectionAnchorRef = useRef<number | null>(null);

    const handleFocus = useHandleFocus({ menuRef, setIsFocused, setMenuOpen });

    const handleBlur = useHandleBlur({ setIsFocused });

    const handleChange = useHandleChange({ menuRef, setSearchValue });

    const handleManualCursorAndSelection = useHandleManualCursorAndSelection({ selectionAnchorRef, lockActiveState });

    const handleArrowKeyAnchorSync = useHandleArrowKeyAnchorSync({ selectionAnchorRef });

    const handleSelectWordAtCaret = useHandleSelectWordAtCaret();

    const handleFocusInputOnClick = useHandleFocusInputOnClick({ inputRef, lockActiveState, onFocus: handleFocus });

    const handleSyncValueAndClearActiveStates = useHandleSyncValueAndClearActiveStates({
      setMenuOpen,
      selectedOptionsRef,
    });

    const isCustomPlaceholder = Placeholder && typeof Placeholder !== 'string';

    const [hasInputValue, setHasInputValue] = useState(false);

    return (
      <>
        <input
          ref={mergeRefs(inputRef, propsInputRef)}
          id={id}
          disabled={disabled}
          readOnly={!isSearchable}
          name={id}
          type="text"
          aria-label={isSearchable ? 'Search options' : 'Select options'}
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          role="search"
          className={`${styles.input} ${hasSelectedOptions ? styles['input-with-selected-options'] : ''} ${lockActiveState ? styles['input-in-select-active-state'] : ''} ${hidePlaceholderOnInputFocus ? styles['hide-placeholder-on-focus'] : ''} ${disabled ? styles.disabled : ''}`}
          placeholder={isCustomPlaceholder ? undefined : (Placeholder as string)}
          onPointerDown={handleManualCursorAndSelection}
          onKeyUp={isSearchable ? handleArrowKeyAnchorSync : undefined}
          onDoubleClick={isSearchable ? handleSelectWordAtCaret : undefined}
          onClick={handleFocusInputOnClick}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={
            isSearchable
              ? (e) => {
                  handleChange(e);
                  if (hidePlaceholderIfInputHasValue) {
                    setHasInputValue(e.target.value.length > 0);
                  }
                }
              : undefined
          }
          onInput={isSearchable ? handleSyncValueAndClearActiveStates : undefined}
        />
        {isCustomPlaceholder && !(hidePlaceholderIfInputHasValue && hasInputValue) && (
          <div
            className={styles['custom-placeholder']}
            data-hide-on-focus={hidePlaceholderOnInputFocus}
            data-has-value={hasSelectedOptions}
            data-lock-active-state={lockActiveState}
          >
            {typeof Placeholder === 'function' ? <Placeholder /> : Placeholder}
          </div>
        )}
      </>
    );
  }
);

CustomSelectInput.displayName = 'CustomSelectInput';

export default CustomSelectInput;
